import { useContext, useEffect, useRef, useState } from "react";
import { StyleContext } from "../providers/StyleContextProvider";
import { useMediaQuery } from "react-responsive";
import { Helmet } from "react-helmet-async";
import { Footer } from "../components/Shared/Footer";
import { ForRecruitersTexts as Texts } from '../helpers/LayoutTexts';
import { RecruiterHeader } from "../components/Shared/RecruiterHeader";
import { Button } from "../components/Shared/Buttons/Button";
import { useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as LeftArrow } from "../assets/svg/left_arrow.svg";
import { ReactComponent as RightArrow } from '../assets/svg/right_arrow.svg';
import { ReactComponent as HeaderMobile } from "../assets/svg/short-building.svg";
import { ReactComponent as HeaderDesktop } from "../assets/svg/long-bulding.svg";
import { ReactComponent as SMS } from "../assets/svg/sms.svg";
import { ReactComponent as Whatsapp } from "../assets/svg/whatsapp-2.svg";
import { ReactComponent as Database } from "../assets/svg/database.svg";
import { ReactComponent as Target } from "../assets/svg/target.svg";
import { ReactComponent as Email } from "../assets/svg/email.svg";
import { ReactComponent as HangingSign } from "../assets/svg/hanging-sign.svg";
import { ReactComponent as Note } from "../assets/svg/note.svg";
import { ReactComponent as CheckCircle } from "../assets/svg/check-circle.svg";
import { ReactComponent as CheckCircleDisabled } from "../assets/svg/check-circle-disabled.svg";
import { ReactComponent as DropDownIcon } from '../assets/svg/drop_down.svg';
import { ReactComponent as LeftChevron } from "../assets/svg/left-chevron.svg";
import { ReactComponent as RightChevron } from "../assets/svg/right-chevron.svg";
import { ReactComponent as RegisterCoverMobile } from "../assets/svg/long_building_mobile.svg";
import { ReactComponent as RegisterCoverDesktop } from "../assets/svg/register_section_building.svg";
import { ReactComponent as Speaker } from "../assets/svg/speaker.svg";
import { AuthContext } from "../providers/AuthContextProvider";
import { LoggedRecruiter } from "../models/LoggedRecruiter";
import { LogoGrid } from "../components/Shared/LogoGrid";
import { getFeaturedOrganizations } from "../services/HomeService";
import Slider from "react-slick";
import { getPackageDetails, getTestimonials } from "../services/RecruiterService";
import { PackageDetail } from "../models/PackageDetail";
import useErrorPage from "../hooks/useErrorPage";
import { Testimonial } from "../models/Testimonial";
import { TestimonialCard } from "../components/Testimonials/TestimonialCard";
import { Switch } from "../components/Shared/Buttons/Switch";
import { postCandidateLogout } from "../services/CandidateService";
import { LoggedCandidate } from "../models/LoggedCandidate";
import { FAQs } from "../components/HelpAndSupport/FAQs";

export const ForRecruitersPage: React.FC = () => {
    const navigate = useNavigate();
    const styleContext = useContext(StyleContext);    
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("forRecruitersPage")); 
    const authContext = useContext(AuthContext);    
    const recruiter: LoggedRecruiter | null = authContext.getLoggedRecruiter(); 
    const candidate: LoggedCandidate | null = authContext.getLoggedCandidate();  
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });  
    const logoCount: number = isMobile ? 20 : 32; 
    const [toErrorPage] = useErrorPage();
    const [packageDetails, setPackageDetails] = useState<PackageDetail[]>([]);
    const testimonialCount: number = 12;
    const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
    const testimonialSliderRef = useRef<Slider | null>(null);
    const packageSliderRef = useRef<Slider | null>(null);
    const [isUnlimited, setIsUnlimited] = useState(false);
    const [filteredPackageDetails, setFilteredPackageDetails] = useState<PackageDetail[]>([]);
    const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
    const [isExpand, setIsExpand] = useState(false);
    const location = useLocation();
    const fiftyCVPackageId = 2;
    const unlimitedJobPostingFeatureIndex = 1;
    const popularPackageId = 3;
    const annualPackageId = 7;

    useEffect(() => {            
        setStyles(styleContext.getComponentStyle("forRecruitersPage"));
    }, [isMobile]);

    useEffect(() => {           
        getPackageDetailList();
        getTestimonialList();
        if (candidate !== null) {
            localStorage.removeItem("candidate");
            localStorage.removeItem("savedJobIds");
            localStorage.removeItem("token");
            postCandidateLogout(candidate!.candidateId);
        }
    }, []);
    
    useEffect(() => {           
        handlePackageType();
    }, [isUnlimited]);
    
    useEffect(() => {
       if (location.hash) {
            const element = document.querySelector(location.hash);
            if (element) {
                element.scrollIntoView({ block: 'start' });
            }
        }
    }, [location.hash]);
    
    const handleRegisterClick = () => {
        navigate('/recruiter/account/register');
    }

    const handleLoginClick = () => {
        navigate('/recruiter/account/login');     
    }

    const sliderSettings = {
        className: "center",
        centerMode: true,
        infinite: true,
        centerPadding: "0%",
        dots: true,         
        rows: 1,
        speed: 1000,
        autoplay: true,
        autoplaySpeed: 4000,
        pauseOnHover: true,
        slidesToShow: 1,
        slidesToScroll: 1,
        slidesPerRow: 1,
        useTransform: false,
        arrows: false,
        appendDots: (dots: any) => (
            <div style={styles.dotsWrapper}>
                <ul style={styles.dots}>{dots}</ul>
            </div>
        )
    };

    const features = [
        {
            icon: <SMS style={styles.features.iconSMS} />,
            style: styles.greenBackground,
            title: Texts.features[0].title,
            description: Texts.features[0].description
        },
        {
            icon: <Database style={styles.features.iconDatabase} />,
            style: styles.blueBackground,
            title: Texts.features[2].title,
            description: Texts.features[2].description
        },
        {
            icon: <Whatsapp style={styles.features.whatsapp} />,
            style: styles.greenBackground,
            title: Texts.features[7].title,
            description: Texts.features[7].description
        },
        {
            icon: <Target style={styles.features.iconTarget} />,
            style: styles.blueBackground,
            title: Texts.features[3].title,
            description: Texts.features[3].description
        },
        {
            icon: <Email style={styles.features.email} />,
            style: styles.greenBackground,
            title: Texts.features[6].title,
            description: Texts.features[6].description
        },
        {
            icon: <HangingSign style={styles.features.hangingSign} />,
            style: styles.blackBackground,
            title: Texts.features[8].title,
            description: Texts.features[8].description
        },
        {
            icon: <Note style={styles.features.iconNote} />,
            style: styles.greenBackground,
            title: Texts.features[4].title,
            description: Texts.features[4].description
        },
        {
            icon: <Speaker style={styles.features.iconSpeaker} />,
            style: styles.blackBackground,
            title: Texts.features[5].title,
            description: <>{Texts.features[5].description}<a href="https://hrnews.lk" target="_blank" style={styles.hrNewsUrl}>HRNews.lk</a></>
        }
    ]

    async function getPackageDetailList() {        
        await getPackageDetails()
        .then((response: any) => {
            const packageDetailLists: PackageDetail[] = response.packages;
            packageDetailLists.forEach((packageDetail) => {
                packageDetail.featureAvailability = Texts.featureAvailability.filter(x => x.packageId === packageDetail.packageId)[0].availability;
            });
            setPackageDetails(packageDetailLists);
            const cvBasedPackages = packageDetailLists.filter(pkg => pkg.cvLimiit > 0);
            setFilteredPackageDetails(cvBasedPackages);
        })
        .catch((error) => {            
            toErrorPage(error);
        });
    }

    async function getTestimonialList() {        
        await getTestimonials(testimonialCount)
        .then((testimonialList: Testimonial[]) => {            
            setTestimonials(testimonialList);            
        })
        .catch((error) => {            
            toErrorPage(error);
        });
    }

    const nextSlide = () => {
        testimonialSliderRef.current?.slickNext();
    }

    const previousSlide = () => {
        testimonialSliderRef.current?.slickPrev();
    }

    const testimonialSliderSettings = {
        className: isMobile ? "center" : "recruiter-testimonial-slider",
        centerMode: true,
        infinite: true,
        centerPadding: "0%",
        dots: true,         
        rows: 1,
        speed: 1000,
        autoplay: true,      
        autoplaySpeed: 8000,
        pauseOnHover: true,
        slidesToShow: 1,
        slidesToScroll: 1,
        slidesPerRow: isMobile ? 1 : 3,
        useTransform: false,
        appendDots: (dots: any) => (
            <div style={styles.dotsWrapper}>
                <ul style={styles.testimonialSliderDots}>{dots}</ul>
            </div>
        )
    };

    const previousPackage = () => {
        packageSliderRef.current?.slickPrev();
    }

    const nextPackage = () => {
        packageSliderRef.current?.slickNext();
    }

    const handlePackageType = () => {
        if(!isUnlimited){
            const cvBasedPackages = packageDetails.filter(pkg => pkg.cvLimiit > 0);
            setFilteredPackageDetails(cvBasedPackages);
        }
        else{
            const unlimitedPackages = packageDetails.filter(pkg => pkg.cvLimiit === 0);
            setFilteredPackageDetails(unlimitedPackages);
        }
    }

    const handleShowFeatures = () => {
        setIsExpand(prevState => !prevState);
    }

    return (
        <>
            <Helmet>
                <title>{Texts.metaTitle}</title>
                <meta name="description" content={Texts.metaDescription}/>
            </Helmet>
            <RecruiterHeader recruiterLoginEnable={true}/>
            <div style={styles.header}>  
                <hr style={styles.header.line}/>                 
                <Button style={styles.header.backButton} onClick={() => navigate(-1)}><LeftArrow style={styles.header.backButton.icon}/></Button>    
                <div style={styles.header.title}>{Texts.header.title.white}<span style={styles.header.title.blue}>{Texts.header.title.blue}</span></div>
                <div style={styles.header.description}>{isMobile ? Texts.header.descriptionMobile : Texts.header.descriptionDesktop}</div>
                {!recruiter && <div style={styles.header.buttonWrapper}>                
                    <Button type="button" style={{...styles.header.button, ...styles.header.button.register}} onClick={handleRegisterClick}>{"REGISTER"}</Button>
                    <Button type="button" style={{...styles.header.button, ...styles.header.button.login}} onClick={handleLoginClick}>{"LOGIN"}</Button>
                </div>} 
                {isMobile ? <HeaderMobile style={styles.header.backgroundSvg} /> : <HeaderDesktop style={styles.header.backgroundSvg} />}
            </div>
            <div style={styles.services} id="content">
                <div style={styles.services.normalContainer}>
                    <img src={Texts.services.leadingApplication.image} style={styles.services.mainImage} />
                    <div style={styles.services.mainDetailContainer}>
                        <div style={styles.subTitle}>{Texts.services.title}</div>
                        <div style={styles.title}><span style={styles.green}>{Texts.services.leadingApplication.green}</span>{Texts.services.leadingApplication.black}</div>
                        <div style={styles.services.leadingApplicationDescription}>{Texts.services.leadingApplication.description}</div>
                    </div>
                </div>
                <div style={styles.services.filterOutContainer}>
                    <img src={Texts.services.filterOut.image} style={styles.services.filterOutImage} />
                    <div style={styles.services.detailContainer}>
                        <div style={styles.services.heading}>{Texts.services.filterOut.black}<span style={styles.blue}>{Texts.services.filterOut.blue}</span></div>
                        <div style={styles.services.description} dangerouslySetInnerHTML={{ __html: Texts.services.filterOut.description }}></div>
                    </div>
                </div>
                <div style={styles.services.trackCandidateContainer}>
                    <img src={Texts.services.trackCandidates.image} style={styles.services.trackCandidatesImage} />
                    <div style={styles.services.trackCandidatesDetailContainer}>
                        <div style={styles.services.heading}>
                            {Texts.services.trackCandidates.black}<span style={styles.blue}>{Texts.services.trackCandidates.blue}</span>
                        </div>               
                        <div style={styles.services.description} dangerouslySetInnerHTML={{ __html: Texts.services.trackCandidates.description }}></div>
                    </div>
                </div>
                <div style={styles.services.muchNeededContainer}>
                    <img src={Texts.services.muchNeeded.image} style={styles.services.muchNeededImage} />
                    <div style={styles.services.muchNeededDetailContainer}>
                        <div style={styles.services.heading}>{Texts.services.muchNeeded.black}<span style={styles.blue}>{Texts.services.muchNeeded.blue}</span></div>
                        <div style={styles.services.description} dangerouslySetInnerHTML={{ __html: Texts.services.muchNeeded.description }}></div>
                    </div>
                </div>
                <div style={styles.services.onlineQuizzesContainer}>
                    <img src={Texts.services.onlineQuizzes.image} style={styles.services.onlineQuizzesImage} />
                    <div style={styles.services.detailContainer}>
                        <div style={styles.services.heading}>
                            {Texts.services.onlineQuizzes.black}<span style={styles.blue}>{Texts.services.onlineQuizzes.blue}</span>
                        </div>
                        <div style={styles.services.description} dangerouslySetInnerHTML={{ __html: Texts.services.onlineQuizzes.description }}></div>
                    </div>
                </div>
                <div style={styles.services.aiAssistantContainer}>
                    <img src={Texts.services.aiAssistent.image} style={styles.services.aiAssistantImage} />
                    <div style={styles.services.detailContainer}>
                        <div style={styles.services.heading}>{Texts.services.aiAssistent.black}<span style={styles.blue}>{Texts.services.aiAssistent.blue}</span> {Texts.services.aiAssistent.blackStar}</div>
                        <div style={styles.services.description} dangerouslySetInnerHTML={{ __html: Texts.services.aiAssistent.description }}></div>
                    </div>
                </div>
            </div>
            <div style={styles.cvLess}>
                <div style={styles.cvLess.videoContainer}>
                    <iframe style={styles.cvLess.video} src={Texts.cvLess.youTubeVideo}  frame-border="0" data-allow="autoplay; encrypted-media" allowFullScreen></iframe>
                </div>
                <div style={styles.cvLess.content}>
                    <div style={styles.subTitle}>{Texts.cvLess.subTitle}</div>
                    <div style={styles.title}>{Texts.cvLess.title.black}<span style={styles.green}>{Texts.cvLess.title.green}</span></div> 
                    <div style={styles.cvLess.description} dangerouslySetInnerHTML={{ __html: Texts.cvLess.description }}></div>                   
                </div>
            </div>
            <div style={styles.features} id="campaigns">
                {isMobile ? <div style={styles.features.slider}>
                    <Slider {...sliderSettings}>            
                        {features.map((feature, index) => 
                            <div key={index}>
                                <div style={styles.features.card}>
                                    <div style={{...styles.features.iconWrapper, ...feature.style}}>{feature.icon}</div>
                                    <div style={styles.features.title}>{feature.title}</div>
                                    <div style={styles.features.description}>{feature.description}</div>
                                </div>
                            </div>                    
                        )}                 
                    </Slider> 
                </div>                 
                : <div style={styles.features.grid}>                                                
                    {features.map((feature, index) =>                    
                        <div key={index}>
                            <div style={styles.features.card}>
                                <div style={{...styles.features.iconWrapper, ...feature.style}}>{feature.icon}</div>
                                <div style={styles.features.title}>{feature.title}</div>
                                <div style={styles.features.description}>{feature.description}</div>
                            </div>
                        </div>                    
                    )}                                         
                </div>} 
            </div>
            <div style={styles.careers}>                
                <img src={Texts.careers.image} style={styles.careers.image} />
                <div style={styles.careers.wrapper}>
                    <div style={styles.careers.heading}>
                        {Texts.careers.title.black}<br />
                        <span style={styles.green}>{Texts.careers.title.green}</span>
                    </div>
                    <div style={styles.careers.description}>{Texts.careers.description}</div>
                </div>
            </div>
            <div style={styles.packages} id="pricing-table">
                <div style={{...styles.subTitle, ...styles.packages.subTitle}}>{Texts.packages.subTitle}</div>
                <div style={{...styles.title, ...styles.packages.title}}>{Texts.packages.title.black}<span style={styles.blue}>{Texts.packages.title.blue}</span></div>
                <div style={styles.packages.packageTypeButton}>
                    <span style={styles.packages.packageTypeButton.cvBased}>{Texts.packagetypes.cvBased}</span>
                    <Switch checked={isUnlimited} styles={styles.packages.toggleButton} handleClick={() => {setIsUnlimited(!isUnlimited)}}/>
                    <span style={styles.packages.packageTypeButton.unlimited}>{Texts.packagetypes.unlimited}</span>
                </div>  
                {isMobile ? <>
                    <Slider {...sliderSettings}  ref={packageSliderRef}>            
                        {filteredPackageDetails.map((packageDetail, index) => 
                            <div key={index}>
                                <div style={{...styles.packages.card, ...(packageDetail.packageId === popularPackageId && styles.packages.card.popular)}}>
                                    {packageDetail.packageId === popularPackageId && <div style={styles.packages.popular}>{"Most Popular"}</div>}
                                    <div style={{...styles.packages.cvLimit,  ...(packageDetail.cvLimiit === 0 && styles.packages.textRed)}}>
                                        {packageDetail.cvLimiit !== 0 ? "Up to " + packageDetail.cvLimiit + " CVs" : (packageDetail.price === 0 ? "Unlimited CVs" : "Unlimited CVs Per Month")}
                                    </div>
                                    <div style={{...styles.packages.price, ...(packageDetail.cvLimiit === 0 && styles.packages.textRed)}}>
                                        {isMobile && <LeftChevron style={styles.packages.chevronLeftIcon} onClick={previousPackage}/>}
                                        {(packageDetail.packageId !== annualPackageId) ? packageDetail.price + "LKR + VAT" : packageDetail.packageName}
                                        {isMobile && 
                                            <RightChevron 
                                                style={{...styles.packages.chevronRightIcon, 
                                                    ...(packageDetail.packageId === annualPackageId && styles.packages.annualChevronRightIcon)
                                                }} 
                                                onClick={nextPackage}
                                            />
                                        }
                                    </div>
                                    {Texts.packageFeatures.map((feature, innerIndex) => 
                                        <div 
                                            key={innerIndex} 
                                            style={{
                                                ...styles.packages.featureEnable, 
                                                ...(!packageDetail.featureAvailability[innerIndex] && !(packageDetail.packageId === fiftyCVPackageId && innerIndex === unlimitedJobPostingFeatureIndex) && styles.packages.featureDisable)
                                            }}
                                        >
                                            {packageDetail.packageId === fiftyCVPackageId && innerIndex === unlimitedJobPostingFeatureIndex ? "Two job posting" : feature.feature}
                                        </div>
                                    )}
                                </div>
                                <Button style={styles.packages.button} onClick={handleLoginClick}>{"GET STARTED"}</Button>
                            </div>                    
                        )}                 
                    </Slider> 
                </>                 
                : <> 
                    <table style={styles.packages.table}>
                        <thead>
                            <tr>
                                <th style={styles.packages.included}><span style={styles.packages.tableHeading}>{"What’s Included"}</span></th>
                                {filteredPackageDetails.map((packageDetail, index) => 
                                    <th key={index} 
                                        style={{
                                            ...styles.packages.tableHead, 
                                            ...((hoveredIndex === index) && styles.packages.tableHead.hovered), 
                                            ...(!(hoveredIndex === index) && (packageDetail.packageId === popularPackageId) && styles.packages.headBorderBlack),
                                            ...((hoveredIndex !== -1 && hoveredIndex === index - 1) && styles.packages.tableHead.nextColumnHovered),
                                            ...((hoveredIndex !== -1 && hoveredIndex === index + 1) && styles.packages.tableHead.prevColumnHovered)
                                        }} 
                                        onMouseEnter={() => setHoveredIndex(index)} 
                                        onMouseLeave={() => setHoveredIndex(-1)}
                                    >                                                                    
                                        <div 
                                            style={{
                                                ...styles.packages.cvLimit,
                                                ...(packageDetail.cvLimiit === 0 && styles.packages.unlimitedCVText), 
                                                ...(packageDetail.packageId === popularPackageId && styles.packages.textBlue), 
                                                ...(packageDetail.cvLimiit === 0 && styles.packages.textRed)                                                    
                                            }}
                                        >
                                            {packageDetail.cvLimiit !== 0 ? "Up to " + packageDetail.cvLimiit + " CVs" : (packageDetail.price === 0 ? "Unlimited CVs" : "Unlimited CVs Per Month")}
                                        </div>     
                                        <div 
                                            style={{
                                                ...styles.packages.price, 
                                                ...(packageDetail.packageId === popularPackageId && styles.packages.textBlue), 
                                                ...(packageDetail.cvLimiit === 0 && styles.packages.textRed),
                                                ...(packageDetail.cvLimiit === 0 && styles.packages.unlimitedCVPrice)
                                            }}
                                        >
                                            {(packageDetail.packageId !== annualPackageId) ? packageDetail.price : <span>Bi-Annual / Annual /<br/>Group</span>}
                                            {(packageDetail.packageId !== annualPackageId) && 
                                                <span 
                                                    style={{
                                                        ...styles.packages.lkr, 
                                                        ...(packageDetail.packageId === popularPackageId && styles.packages.textBlue), 
                                                        ...(packageDetail.cvLimiit === 0 && styles.packages.unlimitedCVPrice), 
                                                        ...(packageDetail.cvLimiit === 0 && styles.packages.textRed)
                                                    }}
                                                >
                                                    {"LKR + VAT"}
                                                </span>
                                            }
                                        </div>
                                        {(hoveredIndex !== index) && packageDetail.packageId === popularPackageId && <div style={styles.packages.popular}>{"Most Popular"}</div>}                                    
                                        {(hoveredIndex === index) && 
                                        <Button 
                                            style={{
                                                ...styles.packages.button,
                                                ...(packageDetail.cvLimiit === 0 && styles.packages.unlimitedCVButton), 
                                                ...(packageDetail.cvLimiit === 0 && packageDetail.price === 0 && styles.packages.annualCVButton) 
                                            }} 
                                            onClick={handleLoginClick}
                                        >
                                            {"GET STARTED"}
                                        </Button>}
                                    </th>                    
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {Texts.packageFeatures.map((feature, rowIndex) => 
                                <tr key={rowIndex}>
                                    <td style={styles.packages.body}><span style={styles.packages.featureEnable}>{feature.feature}</span></td>
                                    {filteredPackageDetails.map((packageDetail, cellIndex) => 
                                        <td key={cellIndex} 
                                            style={{
                                                ...styles.packages.cellWrapper, 
                                                ...((hoveredIndex === cellIndex) && styles.packages.cellWrapper.hovered), 
                                                ...((hoveredIndex === cellIndex) && (rowIndex === 0) && styles.packages.table.firstRow), 
                                                ...((hoveredIndex === cellIndex) && (rowIndex === 14) && styles.packages.table.lastRow), 
                                                ...(!(hoveredIndex === cellIndex) && (packageDetail.packageId === popularPackageId) && styles.packages.bodyBorderBlack),
                                                ...((hoveredIndex !== -1 && hoveredIndex === cellIndex - 1) && styles.packages.cellWrapper.nextColumnHovered),
                                                ...((hoveredIndex !== -1 && hoveredIndex === cellIndex + 1) && styles.packages.cellWrapper.prevColumnHovered)
                                            }} 
                                            onMouseEnter={() => setHoveredIndex(cellIndex)} 
                                            onMouseLeave={() => setHoveredIndex(-1)}
                                        >
                                            {packageDetail.packageId === fiftyCVPackageId && rowIndex === unlimitedJobPostingFeatureIndex ? 
                                                <span style={styles.packages.twoJobsPostingWrapper}>
                                                    <span style={{...styles.packages.featureEnable, ...styles.packages.twoJobsPosting}}>{"2 Jobs"}</span>
                                                </span>
                                                : packageDetail.featureAvailability[rowIndex] ? <CheckCircle style={styles.packages.enableIcon} /> : <CheckCircleDisabled style={styles.packages.disableIcon} />}
                                        </td>
                                    )}
                                </tr>
                            )}
                        </tbody>
                    </table>                                                          
                </>}
                <div style={styles.crossPublishing}>
                    <p style={styles.crossPublishing.description}>
                        <span style={styles.crossPublishing.descriptionMark}>{"* "}</span>
                        <span dangerouslySetInnerHTML={{ __html: Texts.crossPublishing.description }} />
                    </p>
                    <div style={styles.crossPublishing.brandList}>
                        {Texts.crossPublishing.brands.map((brand) => (
                            <a 
                                key={brand.name} 
                                href={brand.url} 
                                target="_blank" 
                                rel="noreferrer" 
                                style={{
                                    ...styles.crossPublishing.brandLink,
                                    ...(brand.name === "MyJobs.lk" && styles.crossPublishing.brandLink.myJobs),
                                    ...(brand.name === "HRNews.lk" && styles.crossPublishing.brandLink.hrNews)
                                }}
                            >
                                <img src={brand.image} alt={brand.name} style={styles.crossPublishing.brandLogo} />
                            </a>
                        ))}
                    </div>
                </div> 
            </div>
            <div style={styles.specialPackage}> 
                <div style={{...styles.specialPackage.background, ...(isExpand && styles.specialPackage.background.expand)}}>
                    <div>
                        <div style={styles.specialPackage.title}>{Texts.specialPackage.title}</div>
                        <div style={styles.specialPackage.description}>{Texts.specialPackage.description}</div>
                        <Button style={styles.specialPackage.button} onClick={handleLoginClick}>{"GET STARTED"}</Button>
                        {isMobile && <div style={styles.specialPackage.expand} onClick={handleShowFeatures}>
                            {Texts.specialPackage.expand}
                            <DropDownIcon style={{...styles.specialPackage.expandIcon, ...(isExpand && styles.specialPackage.expandIcon.up)}} />
                        </div>}      
                    </div>
                    <div style={styles.specialPackage.slider}>
                        {isMobile ? 
                            (isExpand && <Slider {...sliderSettings} className={`slider-dots-white ${isExpand ? "expanded" : ""}`}>
                                {Texts.specialPackage.featuresList.map((featuresList, featuresListIndex) => (
                                    <div key={featuresListIndex}>
                                        <div style={styles.specialPackage.featureTitle}>{featuresList.featureTopic}</div>
                                        <ul>
                                            {featuresList.features.map((feature, index) => (
                                                <li key={index} style={styles.specialPackage.features}>
                                                    <div style={styles.specialPackage.featureText}>{feature.feature}</div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </Slider>)
                        :
                        <div style={styles.specialPackage.featuresContainer} >
                            {Texts.specialPackage.featuresList.map((featuresList, featuresListIndex) => (
                                <div key={featuresListIndex}>
                                    <div style={styles.specialPackage.featureTitle}>{featuresList.featureTopic}</div>
                                    <ul>
                                        {featuresList.features.map((feature, index) => (
                                            <li key={index} style={styles.specialPackage.features}>
                                                <div style={styles.specialPackage.featureText}>{feature.feature}</div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>}              
                    </div>
                </div>
            </div>
            <div style={styles.review}>                                    
                <div style={styles.review.buttonWrapper}>
                    <Button style={styles.review.leftButton} onClick={previousSlide}><LeftArrow style={styles.review.buttonIcon} /></Button>
                    <Button style={styles.review.rightButton} onClick={nextSlide}><RightArrow style={styles.review.buttonIcon} /></Button>
                </div>
                <div style={styles.review.slider}>                
                    <Slider ref={testimonialSliderRef} {...testimonialSliderSettings}>
                        {testimonials.map((testimonial, index) =>
                            <div key={index}>
                                <TestimonialCard styles={styles.review.card} testimonial={testimonial}/>                
                            </div>                            
                        )}
                    </Slider>
                </div>
            </div>
            <div style={styles.faqs} id="faq">
                <div style={styles.faqs.content}>
                    <div style={styles.subTitle}>{Texts.faqs.subTitle}</div>
                    <div style={{...styles.title, ...styles.faqs.title}}>
                        {Texts.faqs.title.black}
                        <span style={styles.green}>{Texts.faqs.title.green}</span>
                        <span style={styles.greenToBlack}>{Texts.faqs.title.greenToBlack}</span>
                    </div> 
                    <div style={styles.faqs.description} dangerouslySetInnerHTML={{ __html: Texts.faqs.description}}></div>                   
                </div>
                <div style={styles.faqs.questions}>
                    <FAQs styles={styles.faqs.FAQs} />
                </div>
            </div>
            <div style={styles.register}>
                <div style={styles.register.text}>{Texts.register.text}</div>
                <Button style={styles.register.button} onClick={handleRegisterClick}>{"REGISTER"}</Button>
                {isMobile ? <RegisterCoverMobile style={styles.register.backgroundSvg} /> : <RegisterCoverDesktop style={styles.register.backgroundSvg} />} 
            </div>
            <div style={styles.bestHire}>
                <div style={styles.bestHire.content}>
                    <div style={styles.subTitle}>{Texts.bestHire.subTitle}</div>
                    <div style={styles.title}>
                        {Texts.bestHire.title.black}
                        <span style={styles.blue}>{Texts.bestHire.title.blue}</span>                        
                    </div> 
                    <div style={styles.bestHire.description}>{Texts.bestHire.description}</div>                   
                </div>
                <iframe style={styles.bestHire.video} src={Texts.bestHire.videoURL} frame-border="0" data-allow="autoplay; encrypted-media" allowFullScreen></iframe>
            </div>
            <LogoGrid styles={styles.logoGride} loadOrganizations={getFeaturedOrganizations} recordsCount={logoCount} rowsCount={2} isSlider={isMobile}/>
            <Footer/>
        </>
    );
}
